import type { PostureAnalysis } from '@/lib/types';
import type { AnalysisData } from '@/lib/posture-analysis/posture-pic-evaluation/types';
import { getEvaluationRules } from '@/lib/posture-analysis/evaluation-rules-load';
import {
  getKeyString,
  getRawValue,
} from '@/lib/posture-analysis/posture-pic-evaluation/strategies/posture-analysis-metrics-parse';
import { FHP_LABEL, getLateralInfo, getOrientationInfo } from './self-assessment-report-content';

export interface SimpleSaMetricTile {
  label: string;
  value: string;
  qualifier: string;
}

export interface SimpleSaLiveSummary {
  patternTitle: string;
  compensationsLine: string;
  shoulderTiltLabel: string;
  tibialInclinationLabel: string;
  tiles: SimpleSaMetricTile[];
}

function formatDeg(value: number): string {
  return `${Math.abs(value).toFixed(1)}°`;
}

function metricOrDash(value: number, suffix = ''): string {
  if (!Number.isFinite(value)) return '—';
  return `${formatDeg(value)}${suffix}`;
}

function fhpThresholdDeg(): number {
  const rules = getEvaluationRules() as { posture?: { thresholds?: { fhpSwaybackDegMin?: number } } };
  return rules.posture?.thresholds?.fhpSwaybackDegMin ?? 10;
}

function hasForwardHead(
  analysis: PostureAnalysis,
  cvAngle: number,
): boolean {
  const signals = analysis.classification?.signals ?? [];
  if (signals.some((s) => /forward\s*head/i.test(s))) return true;
  return Number.isFinite(cvAngle) && cvAngle >= fhpThresholdDeg();
}

function kneeQualifier(kneeStr: string, kneeDeg: number): string {
  if (!Number.isFinite(kneeDeg)) return 'Unavailable';
  if (kneeStr.includes('Flex')) return 'Flexed';
  if (kneeStr.includes('Hyperext')) return 'Hyperextended';
  return 'Neutral';
}

function fhpQualifier(cvAngle: number): string {
  if (!Number.isFinite(cvAngle)) return 'Unavailable';
  return cvAngle >= fhpThresholdDeg() ? 'Present' : 'Within range';
}

function trunkHipQualifier(offsetStr: string, offsetDegOrCm: number): string {
  if (!Number.isFinite(offsetDegOrCm)) return 'Unavailable';
  if (offsetStr.includes('Ant')) return 'Anterior';
  if (offsetStr.includes('Post')) return 'Posterior';
  return 'Neutral';
}

/**
 * Maps a live `PostureAnalysis` (v4 classification + per-view metrics) into
 * the Step 3 summary card. Returns null until classification exists — never
 * invents a pattern or angles.
 */
export function buildSimpleSaLiveSummary(
  analysis: PostureAnalysis | null | undefined,
): SimpleSaLiveSummary | null {
  const pattern = analysis?.classification?.pattern;
  if (!analysis || !pattern) return null;

  const analysisData = (analysis.analysis ?? {}) as AnalysisData;
  const orientation = getOrientationInfo(pattern);
  const lateral = getLateralInfo(analysis.classification?.lateralized);

  const shoulderTilt = getRawValue(analysisData, 'Anterior', 'Shoulders level');
  const shoulderStr = getKeyString(analysisData, 'Anterior', 'Shoulders level');
  const tibial = getRawValue(analysisData, 'Right', 'Tibial Inclination');
  const tibialStr = getKeyString(analysisData, 'Right', 'Tibial Inclination');
  const kneeDeg = getRawValue(analysisData, 'Right', 'Knee State');
  const kneeStr = getKeyString(analysisData, 'Right', 'Knee State');
  const cvAngle = getRawValue(analysisData, 'Right', 'Craniovertebral');
  const trunkHip = getRawValue(analysisData, 'Right', 'TrunkHip Offset') ||
    getRawValue(analysisData, 'Right', 'Trunk–Hip Offset');
  const trunkHipStr =
    getKeyString(analysisData, 'Right', 'TrunkHip Offset') ||
    getKeyString(analysisData, 'Right', 'Trunk–Hip Offset');

  const compensations: string[] = [orientation.label];
  if (hasForwardHead(analysis, cvAngle)) {
    compensations.push(FHP_LABEL.label);
  }
  if (lateral.label && !/well-balanced/i.test(lateral.label)) {
    compensations.push(lateral.label);
  }

  const shoulderDir = shoulderStr.includes('Right')
    ? ' Right'
    : shoulderStr.includes('Left')
      ? ' Left'
      : '';
  const tibialDir = tibialStr.includes('Ant')
    ? ' Ant'
    : tibialStr.includes('Post')
      ? ' Post'
      : '';

  return {
    patternTitle: orientation.label,
    compensationsLine: `Key Compensations Identified: ${compensations.join(', ')}.`,
    shoulderTiltLabel: Number.isFinite(shoulderTilt)
      ? `Shoulder Tilt: ${formatDeg(shoulderTilt)}${shoulderDir}`
      : 'Shoulder Tilt: —',
    tibialInclinationLabel: Number.isFinite(tibial)
      ? `Tibial Inclination: ${formatDeg(tibial)}${tibialDir}`
      : 'Tibial Inclination: —',
    tiles: [
      {
        label: 'Knee Flex',
        value: metricOrDash(kneeDeg),
        qualifier: kneeQualifier(kneeStr, kneeDeg),
      },
      {
        label: 'Forward Head',
        value: metricOrDash(cvAngle),
        qualifier: fhpQualifier(cvAngle),
      },
      {
        label: 'Trunk–Hip Offset',
        value: Number.isFinite(trunkHip) ? `${Math.abs(trunkHip).toFixed(1)}` : '—',
        qualifier: trunkHipQualifier(trunkHipStr, trunkHip),
      },
    ],
  };
}
